import { InquiryButtonLink as ButtonLink } from "./inquiry-origin.js";
import { InquiryLink as Link } from "./inquiry-origin.js";
import { ArrowRight } from "lucide-react";

import { Eyebrow, SectionHeading } from "@zylarity/ui";

export function RoleFamilies() {
  return (
    <section className="public-shell recruiting-resources" aria-labelledby="role-families-title">
      <Eyebrow>Specialist roles. Real operating context.</Eyebrow>
      <h2 id="role-families-title">OT Cybersecurity roles we recruit for.</h2>
      <p>
        Critical systems need people who understand how security decisions affect safety,
        availability and operations. A broad Cybersecurity keyword search can miss that experience.
        We shape each search around the work, the environment and the people the role supports.
      </p>
      <ul className="role-family-list">
        <li>OT / ICS security engineers</li>
        <li>Industrial security architects</li>
        <li>OT security consultants and assessors</li>
        <li>Industrial network security specialists</li>
        <li>OT detection and incident response specialists</li>
        <li>OT security leaders and program owners</li>
      </ul>
      <p className="role-family-note">
        <span className="role-family-note__mark" aria-hidden="true">
          *
        </span>
        <span>
          <strong>Examples, not an exhaustive list.</strong> Titles vary between employers. Bring us
          the responsibilities and constraints; we will help define the search.
        </span>
      </p>
    </section>
  );
}

export function ProfessionalChoices() {
  return (
    <section className="public-shell professional-choices" aria-label="Your next steps">
      <article>
        <Eyebrow>Your plans can change</Eyebrow>
        <h2>Stay in control of your next move.</h2>
        <p>Return with the same email to update your profile, preferences or interest in a role.</p>
        <Link className="text-link" to="/intake/candidate">
          Update my profile or interests <ArrowRight size={16} aria-hidden="true" />
        </Link>
      </article>
      <article>
        <Eyebrow>A fresh perspective</Eyebrow>
        <h2>See the strengths behind your experience.</h2>
        <p>
          Request a free OT career snapshot, reviewed by a recruiter and subject to availability.
          Explore relevant role families and useful development questions.
        </p>
        <Link className="text-link" to="/professionals/career-snapshot">
          Get my OT career snapshot <ArrowRight size={16} aria-hidden="true" />
        </Link>
      </article>
      <article>
        <Eyebrow>Introduce an OT professional</Eyebrow>
        <h2>Know someone ready for their next move?</h2>
        <p>
          Help a friend take their next step and receive up to $500 toward eligible professional
          development after a qualifying Zylarity placement. Terms and eligibility apply.
        </p>
        <Link className="text-link" to="/refer">
          Refer a friend <ArrowRight size={16} aria-hidden="true" />
        </Link>
      </article>
    </section>
  );
}

export function RecruitingFaqs() {
  return (
    <section
      className="public-shell recruiting-resources recruiting-faq"
      aria-labelledby="recruiting-faq-title"
    >
      <Eyebrow>Before we talk</Eyebrow>
      <h2 id="recruiting-faq-title">A few useful answers.</h2>
      <details>
        <summary>What makes OT recruiting different?</summary>
        <p>
          Technical experience has to fit a physical operating environment. Safety, uptime, legacy
          technology, access to sites and coordination with operations can matter as much as a
          familiar security tool. We explore responsibilities and decisions a person can explain.
        </p>
      </details>
      <details>
        <summary>Do we need a finished job description?</summary>
        <p>
          No. Start with the outcome you need, the operating environment and the constraints. We can
          help turn those into a role profile before the search begins.
        </p>
      </details>
      <details>
        <summary>How do fees and engagement terms work?</summary>
        <p>
          You can ask about a role without committing to a search. We confirm the scope, fees and
          terms before you authorize a recruiting engagement. Standalone assessments are priced
          separately; the agreed candidate assessment is included when we recruit for the role.
        </p>
      </details>
      <details>
        <summary>How quickly can you fill a role?</summary>
        <p>
          Timing depends on the role, location, compensation, availability and decision process. We
          agree a search plan and communication rhythm after understanding the role profile, and
          explain progress and obstacles as the search develops.
        </p>
      </details>
      <details>
        <summary>What happens after an offer is accepted?</summary>
        <p>
          We stay in touch through the notice period, start and early onboarding. Pre-start, 30-day
          and 90-day conversations help both sides raise concerns and keep expectations aligned. The
          agreed recruiting engagement defines the support provided.
        </p>
      </details>
      <details>
        <summary>Does a professional pay to be considered for a job?</summary>
        <p>
          No. Professionals do not pay to express interest, receive an employment offer or request
          an available career snapshot. Employer assessments are paid services; the agreed candidate
          assessment is included when Zylarity recruits for the role.
        </p>
      </details>
    </section>
  );
}

export function RecruitmentSafetyPage() {
  return (
    <section className="public-shell recruiting-resources resource-article">
      <Eyebrow>Recruitment safety</Eyebrow>
      <h1>Check a message claiming to be from Zylarity.</h1>
      <p>
        If a message claiming to be from Zylarity seems unusual, pause and verify it using our
        website or <a href="mailto:support@zylarity.com">support@zylarity.com</a>.
      </p>
      <h2>Check the source</h2>
      <p>
        Our website is zylarity.com. Our recruiter email addresses end in @zylarity.com. Check the
        full address, not only the display name. A familiar name, logo or social account is not
        enough to establish that a message came from us.
      </p>
      <h2>No payment for a job offer</h2>
      <p>
        We do not ask professionals to pay an application fee, buy equipment from a nominated
        seller, send cryptocurrency or gift cards, or pay to receive an employment offer. A
        separately agreed talent assessment is never a requirement for employment consideration.
      </p>
      <h2>Protect your information</h2>
      <p>
        Use the secure intake linked from this website for your professional details. Never send
        passwords or email verification codes to a recruiter. Verify an unexpected request for
        identity or banking information before responding.
      </p>
      <h2>Report a concern</h2>
      <p>
        Send the sender address and a short description to our support address. Avoid forwarding
        identity documents, payment details or other sensitive attachments. If money or an account
        has been compromised, contact the relevant bank or account provider promptly.
      </p>
      <ButtonLink href="mailto:support@zylarity.com">Report a suspicious message</ButtonLink>
    </section>
  );
}

export function HiringGuidePage() {
  const path = "https://zylarity.com/intelligence/hiring-critical-ot-roles";
  return (
    <article className="public-shell recruiting-resources resource-article">
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          headline: "Hiring for critical OT environments starts with the right questions.",
          description:
            "Clarify OT responsibilities, explore relevant experience and make the match useful. Practitioner guidance for hiring in critical operating environments.",
          mainEntityOfPage: path,
          url: path,
          datePublished: "2026-09-20",
          dateModified: "2026-09-27",
          author: { "@type": "Organization", name: "Zylarity", url: "https://zylarity.com" },
          publisher: { "@type": "Organization", name: "Zylarity", url: "https://zylarity.com" },
          image: "https://zylarity.com/share/intelligence-insights-hiring-critical-ot-roles.png",
        })}
      </script>
      <Eyebrow>Industry Intelligence / Hiring guidance</Eyebrow>
      <h1>Hiring for critical OT environments starts with the right questions.</h1>
      <p>
        An OT security title rarely tells the whole story. Two employers can advertise the same
        title while needing very different people. Start with the operating responsibility, then
        examine the experience that supports it.
      </p>
      <SectionHeading eyebrow="01 / The role" title="Name the decisions the person will own." />
      <p>
        Will they design segmentation, maintain asset visibility, lead incident coordination or
        build a program across sites? Identify what they can change directly, what requires an
        operations partner and what must be escalated. Add the practical constraints: location,
        travel, on-call expectations, compensation and access to facilities.
      </p>
      <SectionHeading
        eyebrow="02 / The talent"
        title="Explore experience in its original context."
      />
      <p>
        A tool name is a starting point for a conversation. Ask what the professional personally
        did, where they did it, what constraints shaped the decision and how the result was
        understood. Distinguish hands-on ownership from participation, and demonstrated evidence
        from information still to be checked.
      </p>
      <SectionHeading eyebrow="03 / The match" title="Make the unknowns useful." />
      <p>
        A good shortlist explains both the fit and the questions that remain. A candidate may bring
        strong architecture experience but need a conversation about plant access or travel. Another
        may know the environment well but need deeper technical evaluation. Those are specific next
        steps, not reasons to hide uncertainty behind a score.
      </p>
      <p>
        <Link className="text-link" to="/intelligence/insights">
          Three questions for an OT hire <ArrowRight size={16} aria-hidden="true" />
        </Link>
      </p>
      <section className="hiring-guide-planning" aria-labelledby="hiring-plan-title">
        <h2 id="hiring-plan-title">Before you open the search</h2>
        <p>
          <strong>Write down five things:</strong>
        </p>
        <ol>
          <li>
            <strong>The operating environment:</strong> systems, sites and safety or availability
            constraints.
          </li>
          <li>
            <strong>The responsibility:</strong> decisions the person owns and where operations must
            be involved.
          </li>
          <li>
            <strong>The evidence:</strong> work they should be able to explain or demonstrate.
          </li>
          <li>
            <strong>The practical fit:</strong> location, travel, on-call expectations and
            compensation.
          </li>
          <li>
            <strong>The first outcome:</strong> what should be different after 90 days.
          </li>
        </ol>
        <p>You do not need a finished job description to start this conversation.</p>
      </section>
      <section className="hiring-guide-example" aria-labelledby="hiring-example-title">
        <h2 id="hiring-example-title">
          Turn a broad requirement into a useful interview question.
        </h2>
        <dl>
          <dt>Broad requirement:</dt>
          <dd>Experience with OT network segmentation.</dd>
          <dt>Clearer requirement:</dt>
          <dd>
            Design OT network segmentation that preserves required plant communications and approved
            support access.
          </dd>
          <dt>Interview question:</dt>
          <dd>
            Walk us through a segmentation change you worked on. Which traffic needed to cross the
            boundary, who approved those connections, and how did you test the rules and rollback
            plan?
          </dd>
          <dt>What to listen for:</dt>
          <dd>
            What the person personally owned and how the operating constraints changed the plan.
          </dd>
        </dl>
      </section>
      <h2>Regulation can sharpen the brief.</h2>
      <p>
        In the United States, for organizations in scope, NERC's Critical Infrastructure Protection
        (CIP) standards address Cybersecurity for the bulk power system under FERC oversight. For a
        role supporting this work, identify the systems in scope and the responsibilities the person
        will own. Confirm which requirements apply before turning a compliance obligation into a
        hiring requirement.
      </p>
      <p>
        <a
          href="https://www.ferc.gov/industries-data/electric/industry-activities/cyber-and-grid-security"
          target="_blank"
          rel="noopener noreferrer"
        >
          Source: FERC, Cyber and Grid Security.
        </a>
      </p>
      <p>
        In the European Union, for organizations in scope, NIS2-related work can require clear
        responsibilities and appropriate Cybersecurity skills. ENISA maps obligations to role
        profiles and tasks, including staffing and skills development. A recruitment decision can
        support that work; hiring a particular job title does not itself establish compliance.
      </p>
      <p>
        <a
          href="https://www.enisa.europa.eu/publications/cybersecurity-roles-and-skills-for-nis2-essential-and-important-entities"
          target="_blank"
          rel="noopener noreferrer"
        >
          Source: ENISA, Cybersecurity roles and skills for NIS2 Essential and Important Entities.
        </a>
      </p>
      <p>
        <Link className="text-link" to="/our-process/examples?report=role#sample-report-preview">
          See an example OT role profile <ArrowRight size={16} aria-hidden="true" />
        </Link>
      </p>
      <p className="hiring-guide-contact">
        Need help shaping a role? <Link to="/contact?kind=EMPLOYER">Discuss Your Needs</Link>
      </p>
    </article>
  );
}
